import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dealersAPI } from '../../api/api';

export const fetchDealers = createAsyncThunk('dealers/fetchAll', async () => {
  const response = await dealersAPI.getAll();
  return response.data.data;
});

export const fetchDealerById = createAsyncThunk(
  'dealers/fetchById',
  async (id) => {
    const response = await dealersAPI.getById(id);
    return response.data.data;
  },
);

export const createDealer = createAsyncThunk(
  'dealers/create',
  async (data) => {
    const response = await dealersAPI.create(data);
    return response.data.data;
  },
);

export const updateDealer = createAsyncThunk(
  'dealers/update',
  async ({ id, data }) => {
    const response = await dealersAPI.update(id, data);
    return response.data.data;
  },
);

export const deleteDealer = createAsyncThunk('dealers/delete', async (id) => {
  await dealersAPI.delete(id);
  return id;
});

// The backend still persists sync_status/sync_error on the dealer row even
// when the sync itself fails (502), so a failed sync carries the updated
// row too -- surface both via rejectWithValue instead of losing the row.
export const syncDealer = createAsyncThunk(
  'dealers/sync',
  async (id, { rejectWithValue }) => {
    try {
      const response = await dealersAPI.sync(id);
      return response.data.data;
    } catch (err) {
      const body = err.response?.data;
      return rejectWithValue({ error: body?.error || err.message, data: body?.data });
    }
  },
);

const dealersSlice = createSlice({
  name: 'dealers',
  initialState: {
    items: [],
    currentDealer: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrentDealer: (state) => {
      state.currentDealer = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDealers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchDealers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchDealerById.fulfilled, (state, action) => {
        state.currentDealer = action.payload;
      })
      .addCase(createDealer.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateDealer.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(deleteDealer.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(syncDealer.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(syncDealer.rejected, (state, action) => {
        const updated = action.payload?.data;
        if (!updated) return;
        const index = state.items.findIndex((item) => item.id === updated.id);
        if (index !== -1) state.items[index] = updated;
      });
  },
});

export const { clearCurrentDealer, clearError } = dealersSlice.actions;
export default dealersSlice.reducer;
